/**
 * The Website tab as a document: a light, printable A4 report downloaded straight as a `.pdf` (no print dialog). It is
 * drawn with jsPDF (vector text and lines, so the text stays crisp and selectable) and jspdf-autotable (the tables,
 * with their header repeated on each page).
 *
 * A DUMB renderer: it draws whatever {@link WebsiteReport} it is handed, which the tab builds from the same figures it
 * shows, so the document never says anything the page does not. Its colours are fixed (dark ink on white), whatever
 * the studio's theme.
 *
 * jsPDF is loaded on the first export (a dynamic import), never at boot: a static import pulls its whole optional
 * dependency tree into Vite's dev pre-bundle (see vite.config `optimizeDeps`), and it code-splits the library out of
 * the main bundle.
 */

/** One headline figure: its name, value, change against the period before and that change's caption. */
export type ReportKpi = {
  label: string;
  value: string;
  /** "+12.00%" (null: not compared). */
  change: string | null;
  dir: 'up' | 'down' | 'flat';
  caption: string;
  /** The suspected line under the figure ("Includes 12 suspected"); none: no line. */
  suspected?: string;
};

export type ReportColumn = {
  label: string;
  align?: 'left' | 'right';
  /** Share of the table's width, 0..1. */ width?: number;
};

/** One section's table: its title, an optional line under it, and its rows (already formatted). */
export type ReportTable = {
  title: string;
  sub?: string;
  columns: ReportColumn[];
  rows: string[][];
  /** One column's cells open an address (a row's page): drawn as links, with a small arrow. */
  links?: { column: number; urls: (string | null)[] };
  /** Said instead of an empty table. */
  empty: string;
  /** Lines under the table. */
  foot?: string[];
  /** Use the body-muted ink for semantic notes that must remain readable. */
  footTone?: 'readable';
};

/** The trend's line colours (the tab's chart colours, as jsPDF takes them). */
export const REPORT_SERIES_RGB = {
  visitors: [128, 60, 255],
  views: [8, 145, 178],
  suspected: [161, 161, 170],
  excluded: [243, 18, 96],
} as const satisfies Record<string, readonly [number, number, number]>;

/** The visits over the period: one line per series, one point per hour / day. */
export type ReportTrend = {
  title: string;
  labels: string[];
  series: { name: string; rgb: readonly [number, number, number]; points: number[] }[];
};

export type WebsiteReport = {
  /** The studio's lockup for a light page (an image the page serves, so it can be drawn), and the name said instead
   *  when it cannot be drawn. */
  logo: { src: string; alt: string };
  /** The brand's own logo for a light page (its black wordmark, or its primary logo), beside the title; none: no
   *  logo there. */
  brandLogo?: string;
  /** "FortunaMax website". */
  title: string;
  /** "Website analytics". */
  kind: string;
  /** "Exported Oct 16, 2026". */
  exported: string;
  /** "Last 7 days". */
  period: string;
  /** What the numbers are narrowed to (page, source, tracking link). */
  filters: { label: string; value: string }[];
  /** The one line said when no filter is active; none: no line. */
  noFilters?: string;
  /** Every counted address under the address selector's own label, listed whole (never cut). */
  addresses?: { label: string; hosts: string[] };
  /** The screen's count line, word for word; none when the screen shows none. */
  countLine?: string;
  /** The active data notes, in the screen's order. */
  notes?: string[];
  /** The full traffic breakdown table. */
  breakdown?: ReportTable;
  lead: string;
  follow: string[];
  kpis: ReportKpi[];
  trend: ReportTrend | null;
  tables: ReportTable[];
  footnote: string;
  /** Use the body-muted ink for a semantic footnote that must remain readable. */
  footnoteTone?: 'readable';
  /** "Page {{n}} of {{total}}", already translated with the placeholders kept. */
  pageOf: string;
  /** The downloaded file's name, without `.pdf`. */
  fileName: string;
};

type Rgb = [number, number, number];

/* The document's fixed light inks. */
const INK: Rgb = [24, 24, 27];
export const REPORT_READABLE_NOTE_RGB: Rgb = [95, 99, 110];
const GREY = REPORT_READABLE_NOTE_RGB;
const FAINT: Rgb = [150, 154, 164];
const HAIRLINE: Rgb = [226, 228, 233];
const PANEL: Rgb = [247, 247, 250];
const ACCENT: Rgb = [128, 60, 255];
const UP: Rgb = [15, 138, 67];
const DOWN: Rgb = [196, 43, 43];

const PAGE_MARGIN = 16;

/** The lockup as a PNG data URL (jsPDF draws pictures, not SVG), drawn once per address and kept, with its aspect
 *  ratio. A same-origin image, so the canvas can be read back. */
const logoCache = new Map<string, Promise<{ png: string; ratio: number }>>();
const LOGO_TIMEOUT_MS = 8000;

const logoPng = (src: string): Promise<{ png: string; ratio: number }> => {
  const cached = logoCache.get(src);
  if (cached) return cached;
  const made = new Promise<{ png: string; ratio: number }>((resolve, reject) => {
    const img = new Image();
    // The brand's logos live on a CDN that allows any origin: read in CORS mode so the canvas stays readable.
    img.crossOrigin = 'anonymous';
    // A logo that never answers must not hold the download: it is left out after a while.
    const timer = window.setTimeout(() => reject(new Error('logo took too long')), LOGO_TIMEOUT_MS);
    img.onload = () => {
      window.clearTimeout(timer);
      try {
        const ratio = img.naturalWidth / Math.max(1, img.naturalHeight);
        const canvas = document.createElement('canvas');
        canvas.width = 1100; // generous, so the lockup stays crisp at ~46 mm
        canvas.height = Math.round(1100 / ratio);
        const ctx = canvas.getContext('2d');
        if (!ctx || !ratio) {
          reject(new Error('logo could not be drawn'));
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve({ png: canvas.toDataURL('image/png'), ratio });
      } catch (error) {
        // A canvas the image tainted (a host that stopped allowing other origins) cannot be read.
        reject(error);
      }
    };
    img.onerror = () => {
      window.clearTimeout(timer);
      reject(new Error('logo did not load'));
    };
    img.src = src;
  });
  // A failed draw is not kept, so the next export tries again.
  made.catch(() => logoCache.delete(src));
  logoCache.set(src, made);
  return made;
};

/** jsPDF's built-in fonts read Latin-1 only: the few typographic characters the tab uses get their plain forms, and
 *  anything else beyond Latin-1 reads as "?" rather than being dropped silently. Pure. */
export const pdfText = (text: string): string =>
  text
    .replace(/[\u203a\u00bb]/g, '>')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/\u2192/g, '->')
    .replace(/[\u2009\u202f\u00a0]/g, ' ')
    .replace(/[^\t\n\r -~\u00a1-\u00ff]/g, '?');

/** The report as a PDF document (not saved yet). */
export const buildWebsiteReportPdf = async (report: WebsiteReport) => {
  const [{ jsPDF }, { autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const left = PAGE_MARGIN;
  const right = pageW - PAGE_MARGIN;
  const width = right - left;
  const bottom = pageH - PAGE_MARGIN - 6; // the footer's band
  const ink = (rgb: Rgb): void => {
    doc.setTextColor(rgb[0], rgb[1], rgb[2]);
  };
  const font = (style: 'normal' | 'bold', size: number): void => {
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
  };
  /** Wrapped text from `y`; returns the y under it. */
  const para = (text: string, y: number, size: number, style: 'normal' | 'bold', rgb: Rgb, gap = 1.6): number => {
    font(style, size);
    ink(rgb);
    const lines: string[] = doc.splitTextToSize(pdfText(text), width);
    const lineH = size * 0.3528 * 1.35; // pt → mm, with leading
    lines.forEach((line, i) => doc.text(line, left, y + i * lineH));
    return y + lines.length * lineH + gap;
  };
  /** Room for `need` mm from `y`, else a new page (returns where to continue). */
  const room = (y: number, need: number): number => {
    if (y + need <= bottom) return y;
    doc.addPage();
    return PAGE_MARGIN + 4;
  };

  /** One section's table at `y`, with its title and the lines under it; moves `y` under it. */
  const drawTable = (table: ReportTable): void => {
    // A short table stays on one page (its title, header and rows); a long one starts where it can and runs on.
    y = room(y, Math.min(16 + (Math.max(1, table.rows.length) + 1) * 7.6, 130));
    font('bold', 11);
    ink(INK);
    doc.text(pdfText(table.title), left, y);
    y += 1.5;
    if (table.sub) y = para(table.sub, y + 3, 8.5, 'normal', GREY, 0);
    y += 2.5;
    if (table.rows.length === 0) {
      y = para(table.empty, y + 2, 9, 'normal', FAINT, 6);
      return;
    }
    let endY = y;
    const linkOf = (column: number, row: number): string | null =>
      table.links && table.links.column === column ? table.links.urls[row] ?? null : null;
    autoTable(doc, {
      startY: y,
      margin: { left, right: PAGE_MARGIN, top: PAGE_MARGIN + 4, bottom: PAGE_MARGIN + 6 },
      head: [table.columns.map((c) => pdfText(c.label))],
      body: table.rows.map((row) => row.map(pdfText)),
      theme: 'plain',
      styles: {
        font: 'helvetica',
        fontSize: 9,
        textColor: INK,
        cellPadding: { top: 2.2, bottom: 2.2, left: 2.5, right: 2.5 },
      },
      headStyles: { fontStyle: 'bold', fontSize: 8.5, textColor: GREY, fillColor: PANEL },
      alternateRowStyles: { fillColor: [252, 252, 253] },
      columnStyles: Object.fromEntries(
        table.columns.map((c, i) => [
          i,
          { halign: c.align ?? 'left', ...(c.width ? { cellWidth: width * c.width } : {}) },
        ]),
      ),
      didParseCell: (data) => {
        // A thin line under every row, and under the header a slightly darker one.
        data.cell.styles.lineColor = HAIRLINE;
        data.cell.styles.lineWidth = { bottom: data.section === 'head' ? 0.35 : 0.2, top: 0, left: 0, right: 0 };
        if (data.section === 'head') data.cell.styles.halign = table.columns[data.column.index]?.align ?? 'left';
        // A linked cell: the accent ink, and room on its right for the arrow.
        if (data.section === 'body' && linkOf(data.column.index, data.row.index)) {
          data.cell.styles.textColor = ACCENT;
          data.cell.styles.cellPadding = { top: 2.2, bottom: 2.2, left: 2.5, right: 6 };
        }
      },
      didDrawCell: (data) => {
        const url = data.section === 'body' ? linkOf(data.column.index, data.row.index) : null;
        if (!url) return;
        const { x, y: top, width: w, height: h } = data.cell;
        doc.link(x, top, w, h, { url });
        // The arrow (a box with an arrow out of its corner), at the cell's right, on its first line.
        const ax = x + w - 4.6;
        const ay = top + 2.4;
        doc.setDrawColor(ACCENT[0], ACCENT[1], ACCENT[2]);
        doc.setLineWidth(0.25);
        doc.lines(
          [
            [0, 2.2],
            [2.2, 0],
            [0, -0.9],
          ],
          ax,
          ay + 0.6,
          [1, 1],
          'S',
          false,
        );
        doc.line(ax, ay + 0.6, ax + 0.9, ay + 0.6);
        doc.line(ax + 1.1, ay + 1.7, ax + 2.8, ay);
        doc.line(ax + 1.7, ay, ax + 2.8, ay);
        doc.line(ax + 2.8, ay, ax + 2.8, ay + 1.1);
      },
      didDrawPage: (data) => {
        endY = data.cursor?.y ?? endY;
      },
    });
    if (table.foot && table.foot.length > 0) {
      y = endY + 4;
      table.foot.forEach((line) => {
        y = para(line, y, 8, 'normal', table.footTone === 'readable' ? GREY : FAINT, 0.5);
      });
      y += 6;
    } else {
      y = endY + 9;
    }
  };

  // ── Head: the studio's lockup and the kind of report (the receipts' head), then the website, the period and what
  // the numbers are narrowed to ──
  const logoW = 46;
  try {
    const logo = await logoPng(report.logo.src);
    doc.addImage(logo.png, 'PNG', left, 15, logoW, logoW / logo.ratio);
  } catch {
    // The lockup is decorative: a failed draw names the studio instead and never blocks the download.
    font('bold', 15);
    ink(INK);
    doc.text(pdfText(report.logo.alt), left, 22);
  }
  font('bold', 13);
  ink(INK);
  doc.text(pdfText(report.kind), right, 19, { align: 'right' });
  font('normal', 9);
  ink(GREY);
  doc.text(pdfText(report.exported), right, 24.5, { align: 'right' });
  doc.setDrawColor(INK[0], INK[1], INK[2]);
  doc.setLineWidth(0.4);
  doc.line(left, 31, right, 31);

  let y = 42;
  // The brand's own logo at the title band's right (decorative: one that does not draw is left out).
  if (report.brandLogo) {
    try {
      const brand = await logoPng(report.brandLogo);
      const maxW = 48;
      const maxH = 11;
      const w = Math.min(maxW, maxH * brand.ratio);
      const h = w / brand.ratio;
      doc.addImage(brand.png, 'PNG', right - w, 36 + (maxH - h) / 2, w, h);
    } catch {
      // No brand logo in the report.
    }
  }
  font('bold', 17);
  ink(INK);
  doc.text(pdfText(report.title), left, y);
  y += 6;
  font('bold', 11);
  ink(ACCENT);
  doc.text(pdfText(report.period), left, y);
  y += 5;
  if (report.addresses) {
    y = para(`${report.addresses.label}: ${report.addresses.hosts.join(', ')}`, y, 9, 'normal', GREY, 0.5);
  }
  if (report.filters.length > 0) {
    report.filters.forEach((f) => {
      y = para(`${f.label}: ${f.value}`, y, 9, 'normal', GREY, 0.5);
    });
  } else if (report.noFilters) {
    y = para(report.noFilters, y, 9, 'normal', GREY, 0.5);
  }
  if (report.countLine) y = para(report.countLine, y, 8.5, 'normal', FAINT, 0.5);
  if (report.notes && report.notes.length > 0) {
    y += 1.5;
    report.notes.forEach((note) => {
      y = para(`- ${note}`, y, 9, 'normal', INK, 0.5);
    });
  }
  doc.setDrawColor(HAIRLINE[0], HAIRLINE[1], HAIRLINE[2]);
  doc.setLineWidth(0.3);
  doc.line(left, y, right, y);
  y += 8;

  // ── The answer in a sentence ──
  y = para(report.lead, y, 13, 'bold', INK, 1.2);
  report.follow.forEach((line) => {
    y = para(line, y, 10, 'normal', GREY, 0.6);
  });
  y += 4;

  // ── The headline figures, side by side ──
  if (report.kpis.length > 0) {
    y = room(y, 26);
    const gap = 3;
    const boxW = (width - gap * (report.kpis.length - 1)) / report.kpis.length;
    // A suspected line under a figure moves the change and its caption down.
    const shift = report.kpis.some((kpi) => kpi.suspected) ? 4.5 : 0;
    const boxH = 27 + shift;
    report.kpis.forEach((kpi, i) => {
      const x = left + i * (boxW + gap);
      doc.setFillColor(PANEL[0], PANEL[1], PANEL[2]);
      doc.setDrawColor(HAIRLINE[0], HAIRLINE[1], HAIRLINE[2]);
      doc.roundedRect(x, y, boxW, boxH, 2.5, 2.5, 'FD');
      font('normal', 8.5);
      ink(GREY);
      doc.text(pdfText(kpi.label), x + 4, y + 6);
      font('bold', 16);
      ink(INK);
      doc.text(pdfText(kpi.value), x + 4, y + 14);
      if (kpi.suspected) {
        font('normal', 7.5);
        ink(GREY);
        const suspected: string[] = doc.splitTextToSize(pdfText(kpi.suspected), boxW - 8);
        doc.text(suspected[0] ?? '', x + 4, y + 19);
      }
      if (kpi.change) {
        font('bold', 8.5);
        ink(kpi.dir === 'up' ? UP : kpi.dir === 'down' ? DOWN : GREY);
        doc.text(pdfText(kpi.change), x + 4, y + 19.5 + shift);
        font('normal', 7.5);
        ink(FAINT);
        const caption: string[] = doc.splitTextToSize(pdfText(kpi.caption), boxW - 8);
        doc.text(caption[0] ?? '', x + 4, y + 23.5 + shift);
      }
    });
    y += boxH + 9;
  }

  // ── The traffic breakdown, under the figures ──
  if (report.breakdown) drawTable(report.breakdown);

  // ── Visits over the period ──
  const trend = report.trend;
  if (trend && trend.labels.length >= 2) {
    const chartH = 46;
    y = room(y, chartH + 16);
    font('bold', 11);
    ink(INK);
    doc.text(pdfText(trend.title), left, y);
    y += 4;
    const plotL = left + 10;
    const plotR = right;
    const plotT = y + 2;
    const plotB = y + chartH - 8;
    const max = Math.max(1, ...trend.series.flatMap((s) => s.points)) * 1.1;
    font('normal', 7);
    ink(FAINT);
    doc.setDrawColor(HAIRLINE[0], HAIRLINE[1], HAIRLINE[2]);
    doc.setLineWidth(0.2);
    [0, 0.5, 1].forEach((f) => {
      const gy = plotB - (plotB - plotT) * f;
      doc.line(plotL, gy, plotR, gy);
      doc.text(String(Math.round((max / 1.1) * f)), plotL - 2, gy + 1, { align: 'right' });
    });
    const n = trend.labels.length;
    const xAt = (i: number): number => plotL + ((plotR - plotL) * i) / (n - 1);
    const yAt = (v: number): number => plotB - ((plotB - plotT) * v) / max;
    // At most ~8 axis labels, evenly spread, the last always shown.
    const step = Math.max(1, Math.ceil(n / 8));
    trend.labels.forEach((label, i) => {
      if (i % step !== 0 && i !== n - 1) return;
      doc.text(pdfText(label), xAt(i), plotB + 4, { align: i === 0 ? 'left' : i === n - 1 ? 'right' : 'center' });
    });
    trend.series.forEach((s) => {
      doc.setDrawColor(s.rgb[0], s.rgb[1], s.rgb[2]);
      doc.setLineWidth(0.6);
      for (let i = 1; i < s.points.length; i += 1) {
        doc.line(xAt(i - 1), yAt(s.points[i - 1]), xAt(i), yAt(s.points[i]));
      }
    });
    // Legend, under the axis.
    let lx = plotL;
    const ly = plotB + 9;
    trend.series.forEach((s) => {
      doc.setFillColor(s.rgb[0], s.rgb[1], s.rgb[2]);
      doc.circle(lx + 1.2, ly - 1, 1.2, 'F');
      font('normal', 8);
      ink(GREY);
      doc.text(pdfText(s.name), lx + 3.6, ly);
      lx += 3.6 + doc.getTextWidth(pdfText(s.name)) + 7;
    });
    y = ly + 9;
  }

  // ── The tables ──
  report.tables.forEach(drawTable);

  // ── What a visitor is ──
  if (report.footnote) {
    y = room(y, 12);
    para(report.footnote, y, 8, 'normal', report.footnoteTone === 'readable' ? GREY : FAINT, 0);
  }

  // ── Footer on every page: the website, and where the page sits ──
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p += 1) {
    doc.setPage(p);
    doc.setDrawColor(HAIRLINE[0], HAIRLINE[1], HAIRLINE[2]);
    doc.setLineWidth(0.2);
    doc.line(left, pageH - PAGE_MARGIN, right, pageH - PAGE_MARGIN);
    font('normal', 8);
    ink(FAINT);
    doc.text(pdfText(`${report.title} · ${report.period}`), left, pageH - PAGE_MARGIN + 4.5);
    const pageLabel = report.pageOf.replace('{{n}}', String(p)).replace('{{total}}', String(pages));
    doc.text(pdfText(pageLabel), right, pageH - PAGE_MARGIN + 4.5, { align: 'right' });
  }

  return doc;
};

/** Builds the report and downloads it. */
export const downloadWebsiteReport = async (report: WebsiteReport): Promise<void> => {
  const doc = await buildWebsiteReportPdf(report);
  doc.save(`${report.fileName}.pdf`);
};
